import { useEffect, useState } from "react";
import type { CSSProperties } from "react";

/**
 * Compact layout for the TIM Projects and TIM Tasks pages: on laptop-size screens the page
 * content and the TIM dialogs, menus, poppers and tooltips render the way they look at 67%
 * browser zoom; on large screens they keep their original size. The app header/sidebar are
 * never scaled.
 *
 * The scale is exposed to TimCompactLayout.scss through the --tim-scale CSS custom property,
 * used by TimCompactThemeProvider for portal-rendered MUI components, and used here for the
 * JS-computed grid heights.
 */
const COMPACT_SCALE = 0.67;

// Viewport widths in CSS px (i.e. after Windows display scaling and browser zoom). Up to
// COMPACT_UP_TO_WIDTH (laptops, e.g. 1920px at 125% = 1536px) the pages use the 67% look; from
// FULL_SIZE_FROM_WIDTH (large/external monitors) they keep the original 100% size; in between
// the scale grows linearly so there is no jump while resizing.
const COMPACT_UP_TO_WIDTH = 1600;
const FULL_SIZE_FROM_WIDTH = 1920;

// The layout relies on standardized CSS `zoom` (Chrome/Edge 128+, Firefox 126+), where
// getBoundingClientRect() reports zoomed sizes so MUI can position popups against zoomed
// anchors. Engines with legacy zoom semantics (e.g. Safari) keep the original layout.
const supportsStandardizedZoom =
  typeof Element !== "undefined" && "currentCSSZoom" in Element.prototype;

export const getTimLayoutScale = (): number => {
  if (!supportsStandardizedZoom || typeof window === "undefined") return 1;
  const progress = (window.innerWidth - COMPACT_UP_TO_WIDTH) / (FULL_SIZE_FROM_WIDTH - COMPACT_UP_TO_WIDTH);
  const scale = COMPACT_SCALE + (1 - COMPACT_SCALE) * Math.min(1, Math.max(0, progress));
  return Math.round(scale * 100) / 100;
};

/** Current layout scale; updates on window resize, including moving the window to another screen. */
export const useTimLayoutScale = (): number => {
  const [scale, setScale] = useState(getTimLayoutScale);
  useEffect(() => {
    const update = () => setScale(getTimLayoutScale());
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return scale;
};

export const TIM_COMPACT_CLASS = "tim-compact";

export const timCompactStyle = (scale: number): CSSProperties =>
  ({ display: "contents", "--tim-scale": scale } as CSSProperties);

// The global app header is not part of the page, so it is not scaled.
const APP_HEADER_HEIGHT = 64;

/** True when the page runs inside an iframe (e.g. the HighQ custom page). */
export const IS_EMBEDDED = typeof window !== "undefined" && window.self !== window.top;

/**
 * Height of the host page's fixed header (HighQ's RSM bar). Inside an iframe the app header is not
 * rendered; instead the page reserves this much space at its top, which the host header covers once
 * scrollHostPageToTimFrame() has scrolled the iframe to the top of the window.
 */
export const HOST_HEADER_HEIGHT = 75;

/** Scales the page-chrome part of a calculateGridHeight() offset (everything except the top header). */
export const timGridOffset = (offset: number): number =>
  (IS_EMBEDDED ? HOST_HEADER_HEIGHT : APP_HEADER_HEIGHT) +
  Math.round((offset - APP_HEADER_HEIGHT) * getTimLayoutScale());

let hostPageScrolled = false;

/**
 * Scrolls the host page once per page load so the TIM iframe starts at the top of the window.
 * A cross-origin iframe cannot script its host, but the browser lets scrollIntoView() scroll
 * ancestor frames (verified in Chrome and Edge; other browsers simply keep the current position).
 */
export const scrollHostPageToTimFrame = (): void => {
  if (!IS_EMBEDDED || hostPageScrolled) return;
  hostPageScrolled = true;
  try {
    document.documentElement.scrollIntoView({ block: "start" });
  } catch {}
};